import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Printer, FileDown, Clock, ListOrdered } from "lucide-react";
import { Link } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { PeriodChips, reportPrint, reportPdf, PENDING_PERIODS } from "./reportShared";

const COLUMNS = ["OA No.", "OA Date", "Customer / Consignee", "Destination", "Product / Item", "Category", "Qty", "Pending Since", "Days", "Status", "Remarks"];

export default function PendingOrdersReport() {
  const [period, setPeriod] = useState("all");
  const [data, setData] = useState({ rows: [], totalOas: 0, totalRows: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/pending-orders", { params: { period } });
      setData(data);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const periodLabel = PENDING_PERIODS.find((p) => p.key === period)?.label || period;

  return (
    <div className="space-y-4" data-testid="pending-orders-report">
      <div className="card p-4 flex flex-wrap items-center justify-between gap-3">
        <PeriodChips period={period} setPeriod={setPeriod} prefix="pending" />
        <div className="flex gap-2">
          <button className="btn-outline btn-sm" onClick={() => reportPrint(`/reports/pending-orders/html?period=${period}`)} data-testid="pending-print"><Printer className="h-4 w-4" /> Print Report</button>
          <button className="btn-outline btn-sm" onClick={() => reportPdf(`/reports/pending-orders/pdf?period=${period}`, `pending-orders-${period}.pdf`)} data-testid="pending-pdf"><FileDown className="h-4 w-4" /> Download PDF</button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="card p-5 flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><Clock className="h-6 w-6" /></div>
          <div><div className="text-2xl font-head font-bold" data-testid="pending-total-oas">{data.totalOas}</div><div className="text-xs text-slate-500 uppercase">Pending OAs ({periodLabel})</div></div>
        </div>
        <div className="card p-5 flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-slate-900 text-white flex items-center justify-center"><ListOrdered className="h-6 w-6" /></div>
          <div><div className="text-2xl font-head font-bold" data-testid="pending-total-rows">{data.totalRows}</div><div className="text-xs text-slate-500 uppercase">Product Lines Pending</div></div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 font-head font-semibold">Pending Orders — {periodLabel}</div>
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            {COLUMNS.map((c) => <th key={c} className="th">{c}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {!loading && data.rows.length === 0 && <tr><td className="td text-slate-400 text-center" colSpan={COLUMNS.length} data-testid="pending-empty">No pending orders for this period.</td></tr>}
            {data.rows.map((r, i) => (
              <tr key={`${r.oaId}-${i}`} className="hover:bg-slate-50" data-testid={`pending-row-${i}`}>
                <td className="td font-mono font-semibold"><Link to={`/oa/${r.oaId}/details`} className="text-blue-700 hover:underline" data-testid={`pending-oa-${i}`}>{r.oaNumber}</Link></td>
                <td className="td">{r.oaDate || "—"}</td>
                <td className="td">{r.customerName || "—"}</td>
                <td className="td">{r.destination || "—"}</td>
                <td className="td">{r.item}</td>
                <td className="td">{r.category}</td>
                <td className="td">{r.qty}</td>
                <td className="td">{r.pendingSince || "—"}</td>
                <td className="td"><span className={`badge border ${r.pendingDays > 30 ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid={`pending-days-${i}`}>{r.pendingDays}d</span></td>
                <td className="td">{r.status}</td>
                <td className="td text-xs">{r.remarks || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}
